import { useMemo, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useOutletContext } from 'react-router-dom';
import { PROFILE_LIMIT, canCreateProfile } from '@courseai/domain';
import type { ProjectTabContext } from '../ProjectDetail';
import { useT } from '../../i18n';
import {
  GET_PROJECT_PROFILES,
  CREATE_ART_STYLE_PROFILE,
  GET_MEDIA_UPLOAD_URL,
  type ProjectProfilesData,
  type ProjectProfilesVariables,
  type CreateArtStyleProfileData,
  type CreateArtStyleProfileVariables,
  type GetMediaUploadUrlData,
  type GetMediaUploadUrlVariables,
  type ArtStyleProfileFields,
} from '../../graphql/profiles';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// Estilos visuales tab: lists the project's Art_Style_Profiles (name, artStyle,
// colorPalette, aspect ratio/resolution, and which is the default) and — for
// owners/editors — a create form. The form honors the per-project count limit
// (max 20, PROFILE_LIMIT) and the single-default rule enforced server-side
// (Req 10.1, 10.3, 10.5). Viewers see a read-only list.
export function ArtStylesTab(): ReactNode {
  const { projectId, canEdit } = useOutletContext<ProjectTabContext>();
  const { t } = useT();

  const { data, loading, error, refetch } = useQuery<ProjectProfilesData, ProjectProfilesVariables>(
    GET_PROJECT_PROFILES,
    { variables: { projectId } },
  );

  const profiles = useMemo<ReadonlyArray<ArtStyleProfileFields>>(
    () => data?.getProject?.artStyleProfiles ?? [],
    [data],
  );
  const atLimit = !canCreateProfile(profiles.length);

  if (loading && data === undefined) {
    return (
      <div className="tab-panel">
        <p>{t('artStyles.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-panel">
        <p className="form-error">{t('artStyles.loadError', { message: error.message })}</p>
        <button type="button" onClick={() => void refetch()}>
          {t('artStyles.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <div className="tab-panel-header">
        <h3>{t('artStyles.title')}</h3>
        <span className="profile-count">
          {profiles.length} / {PROFILE_LIMIT}
        </span>
      </div>

      {canEdit && (
        <ArtStyleForm
          projectId={projectId}
          atLimit={atLimit}
          hasProfiles={profiles.length > 0}
          onCreated={async () => {
            await refetch();
          }}
        />
      )}

      {profiles.length === 0 ? (
        <p className="tab-placeholder">{t('artStyles.empty')}</p>
      ) : (
        <ul className="profile-list">
          {profiles.map((profile) => (
            <li key={profile.artStyleProfileId} className="profile-card">
              <div className="profile-card-header">
                <span className="profile-card-name">{profile.name}</span>
                {profile.isDefault && (
                  <span className="profile-default-badge">{t('artStyles.default')}</span>
                )}
              </div>
              <dl className="profile-card-fields">
                <div>
                  <dt>{t('artStyles.field.style')}</dt>
                  <dd>{profile.artStyle}</dd>
                </div>
                <div>
                  <dt>{t('artStyles.field.palette')}</dt>
                  <dd>
                    {profile.colorPalette.length > 0 ? (
                      <span className="profile-palette">
                        {profile.colorPalette.map((color, i) => (
                          <span
                            key={`${color}-${i}`}
                            className="profile-swatch"
                            style={{ background: color }}
                            title={color}
                          />
                        ))}
                      </span>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{t('artStyles.field.aspectRatio')}</dt>
                  <dd>{profile.aspectRatio ?? '—'}</dd>
                </div>
                <div>
                  <dt>{t('artStyles.field.resolution')}</dt>
                  <dd>{profile.resolution ?? '—'}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ArtStyleFormProps {
  projectId: string;
  atLimit: boolean;
  hasProfiles: boolean;
  onCreated(): Promise<void> | void;
}

// Owner/editor-only form to create an Art_Style_Profile. Requires name,
// artStyle, and at least one color in the palette; veoPromptHints,
// negativePrompt, aspectRatio, resolution, and referenceImageKeys are optional.
// The "predeterminado" checkbox designates the new profile as the single
// project default (Req 10.5). Creation is disabled at the count limit (Req 10.3).
function ArtStyleForm({
  projectId,
  atLimit,
  hasProfiles,
  onCreated,
}: ArtStyleFormProps): ReactNode {
  const { t } = useT();
  const [name, setName] = useState('');
  const [artStyle, setArtStyle] = useState('');
  const [palette, setPalette] = useState<readonly string[]>([]);
  const [draftColor, setDraftColor] = useState('#3e64ff');
  const [veoPromptHints, setVeoPromptHints] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');
  const [resolution, setResolution] = useState('');
  const [referenceImageKeys, setReferenceImageKeys] = useState<readonly string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDefault, setIsDefault] = useState(!hasProfiles);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const [createProfile, { loading }] = useMutation<
    CreateArtStyleProfileData,
    CreateArtStyleProfileVariables
  >(CREATE_ART_STYLE_PROFILE);
  const [requestUploadUrl] = useMutation<GetMediaUploadUrlData, GetMediaUploadUrlVariables>(
    GET_MEDIA_UPLOAD_URL,
  );

  function addColor(): void {
    const color = draftColor.trim();
    if (color.length > 0 && palette.indexOf(color) === -1) {
      setPalette((prev) => [...prev, color]);
    }
  }

  function removeColor(color: string): void {
    setPalette((prev) => prev.filter((c) => c !== color));
  }

  async function handleFileSelected(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // allow re-selecting the same file
    if (files.length === 0) {
      return;
    }
    setSubmitError(undefined);
    setUploading(true);
    try {
      for (const file of files) {
        const result = await requestUploadUrl({
          variables: {
            input: {
              projectId,
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
            },
          },
        });
        const target = result.data?.getMediaUploadUrl;
        if (target === undefined) {
          throw new Error(t('artStyles.form.uploadError'));
        }
        // PUT the bytes straight to S3 via the presigned URL (Content-Type must
        // match what was signed).
        const put = await fetch(target.url, {
          method: 'PUT',
          headers: { 'content-type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!put.ok) {
          throw new Error(t('artStyles.form.uploadError'));
        }
        setReferenceImageKeys((prev) => [...prev, target.objectKey]);
      }
    } catch (err) {
      setSubmitError(errorMessage(err, t('artStyles.form.uploadError')));
    } finally {
      setUploading(false);
    }
  }

  function removeReference(key: string): void {
    setReferenceImageKeys((prev) => prev.filter((k) => k !== key));
  }

  function resetForm(): void {
    setName('');
    setArtStyle('');
    setPalette([]);
    setDraftColor('#3e64ff');
    setVeoPromptHints('');
    setNegativePrompt('');
    setAspectRatio('');
    setResolution('');
    setReferenceImageKeys([]);
    setIsDefault(false);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(undefined);

    if (atLimit) {
      setSubmitError(t('artStyles.form.limitReached', { limit: PROFILE_LIMIT }));
      return;
    }

    const trimmedName = name.trim();
    const trimmedArtStyle = artStyle.trim();
    if (trimmedName.length === 0 || trimmedArtStyle.length === 0) {
      setSubmitError(t('artStyles.form.requiredFields'));
      return;
    }
    if (palette.length === 0) {
      setSubmitError(t('artStyles.form.paletteRequired'));
      return;
    }

    const trimmedAspectRatio = aspectRatio.trim();
    const trimmedResolution = resolution.trim();
    const trimmedVeoHints = veoPromptHints.trim();
    const trimmedNegative = negativePrompt.trim();
    const references = referenceImageKeys;

    try {
      await createProfile({
        variables: {
          input: {
            projectId,
            name: trimmedName,
            artStyle: trimmedArtStyle,
            colorPalette: palette,
            veoPromptHints: trimmedVeoHints.length > 0 ? trimmedVeoHints : undefined,
            negativePrompt: trimmedNegative.length > 0 ? trimmedNegative : undefined,
            aspectRatio: trimmedAspectRatio.length > 0 ? trimmedAspectRatio : undefined,
            resolution: trimmedResolution.length > 0 ? trimmedResolution : undefined,
            referenceImageKeys: references.length > 0 ? references : undefined,
            isDefault,
          },
        },
      });
      resetForm();
      await onCreated();
    } catch (err) {
      setSubmitError(errorMessage(err, t('artStyles.form.createError')));
    }
  }

  return (
    <form className="profile-form" onSubmit={handleSubmit}>
      <div className="profile-form-grid">
        <label>
          {t('artStyles.form.name')}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('artStyles.form.namePlaceholder')}
            required
          />
        </label>
        <label>
          {t('artStyles.form.style')}
          <input
            type="text"
            value={artStyle}
            onChange={(e) => setArtStyle(e.target.value)}
            placeholder={t('artStyles.form.stylePlaceholder')}
            required
          />
        </label>
        <label>
          {t('artStyles.form.aspectRatio')}
          <input
            type="text"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            placeholder="16:9"
          />
        </label>
        <label>
          {t('artStyles.form.resolution')}
          <input
            type="text"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            placeholder="1920x1080"
          />
        </label>
      </div>
      <div className="profile-form-field">
        <span className="profile-form-label">{t('artStyles.form.palette')}</span>
        <div className="palette-picker">
          <input
            type="color"
            value={draftColor}
            onChange={(e) => setDraftColor(e.target.value)}
            aria-label={t('artStyles.form.paletteColorLabel')}
          />
          <input
            type="text"
            className="palette-hex"
            value={draftColor}
            onChange={(e) => setDraftColor(e.target.value)}
            placeholder="#3e64ff"
          />
          <button type="button" onClick={addColor}>
            {t('artStyles.form.paletteAdd')}
          </button>
        </div>
        {palette.length > 0 && (
          <ul className="palette-swatch-list">
            {palette.map((color) => (
              <li key={color} className="palette-swatch-item">
                <span className="profile-swatch" style={{ background: color }} title={color} />
                <span className="palette-swatch-hex">{color}</span>
                <button
                  type="button"
                  className="palette-swatch-remove"
                  onClick={() => removeColor(color)}
                  aria-label={t('artStyles.form.paletteRemove', { color })}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <label>
        {t('artStyles.form.veoHints')}
        <textarea
          value={veoPromptHints}
          onChange={(e) => setVeoPromptHints(e.target.value)}
          rows={2}
        />
      </label>
      <label>
        {t('artStyles.form.negativePrompt')}
        <textarea
          value={negativePrompt}
          onChange={(e) => setNegativePrompt(e.target.value)}
          rows={2}
        />
      </label>
      <div className="profile-form-field">
        <span className="profile-form-label">{t('artStyles.form.referenceImages')}</span>
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={uploading}
          onChange={(e) => void handleFileSelected(e)}
          aria-label={t('artStyles.form.referenceImages')}
        />
        {uploading && <p className="field-hint">{t('artStyles.form.uploading')}</p>}
        {referenceImageKeys.length > 0 && (
          <ul className="reference-image-list">
            {referenceImageKeys.map((key) => (
              <li key={key} className="reference-image-item">
                <span className="reference-image-name">{key.split('/').pop()}</span>
                <button
                  type="button"
                  className="reference-image-remove"
                  onClick={() => removeReference(key)}
                  aria-label={t('artStyles.form.referenceRemove')}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <label className="profile-form-default">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        {t('artStyles.form.default')}
      </label>
      <div className="profile-form-actions">
        <button type="submit" disabled={loading || atLimit}>
          {loading ? t('artStyles.form.creating') : t('artStyles.form.create')}
        </button>
      </div>
      {atLimit && (
        <p className="field-hint">{t('artStyles.form.limitHint', { limit: PROFILE_LIMIT })}</p>
      )}
      {submitError && <p className="form-error">{submitError}</p>}
    </form>
  );
}
